// =====================================================================
// SALÉSOL — Coffee Book Pages
// =====================================================================
// Source of truth for every coffee, every price, every tasting note in
// the live coffee book.
//
// Sync rule (set 2026-05): prices are mirrored from the Canva print menu
// ("The exotics", design DAHIwgdS67I). Lot data, varietals and notes are
// fact-checked here in code. Producer names live on the in-café
// presentation card, not on this menu.
//
// To update:
//   1. Edit a lot below (price, credit, notes, name).
//   2. Save.
//   3. git add . && git commit -m "menu: <month> '<yy>" && git push.
//   4. Cloudflare Pages rebuilds in ~60s.
//
// Schema reference (per lot):
//   name   — the coffee's name (serif, primary line).
//   price  — number-only, IDR thousand. "40" displays as 40.
//   credit — italic line: varietal · processing · roaster.
//            (No producer names — they go on the presentation card.)
//   notes  — flavour profile. Short, no trailing period.
//
// Page-level:
//   ledeId   — the Bahasa version of `lede` (and `sidebar.textId` for the
//              pull quote). Descriptions are the only thing the language
//              toggle changes; names and tasting notes stay as written.
//   modifier — the price line under the page headline, e.g. 'Iced +5'.
//              The format names (Piccolo, Flat White, …) are deliberately not
//              listed: guests know them and staff talk them through.
//   origin — (Scenes menu) region line shown in the lot detail,
//            e.g. 'Bensa, Sidama · Ethiopia'. Regional approximation.
//   ll     — (Scenes menu) one or more [lat, lon] pairs for the origin globe.
//   roaster— (Scenes menu) 'bsd' (default) | 'mel' (ST. ALi) | 'syd' (ONA);
//            draws the arc from the farm to the roaster.
//
// Headlines:
//   { size, italic }                — pure italic accent (e.g. "Black")
//   { size, prefix, italic }        — roman + italic (e.g. "The microlots")
//   sizes: 'xl' (70px max), 'lg' (56px), 'md' (44px)
// =====================================================================

export const coffeePages = [
  // -------------------------------------------------------------------
  // 0. SLIPCASE — entry point. Tap to open.
  // -------------------------------------------------------------------
  { slug: '', kind: 'slipcase' },

  // -------------------------------------------------------------------
  // 1. FRONT COVER (rust)
  // -------------------------------------------------------------------
  { slug: 'cover', kind: 'cover-front' },

  // -------------------------------------------------------------------
  // 2. ESPRESSO · BLACK
  // -------------------------------------------------------------------
  {
    slug: 'espresso/black',
    kind: 'menu',
    runner: 'I. Espresso',
    eyebrow: 'I. Espresso',
    headline: { size: 'xl', italic: 'Black' },
    lede: 'Single origins where the bean leads. Bright, fruit-forward, and terroir-driven.',
    ledeId: 'Single origin, bean-nya yang bicara. Bright, fruit-forward, terroir yang terasa.',
    pagemark: { section: 'ESPRESSO' },
    modifierKey: 'espressoBlack',
    modifier: 'Iced +5',
    categories: [
      {
        label: 'Signature Black',
        lots: [
          {
            name: 'Aceh Ribang Gayo',
            price: '40',
            credit: 'Ateng/Bourbon/P88 · Natural · Salésol',
            notes: 'Mixed berries, tropical, brown sugar',
            origin: 'Aceh, Sumatra · Indonesia',
            ll: [[4.63, 96.85]],
          },
          {
            // Ran July–September '26 in Ribang Gayo's place. Kept for reinstatement:
            // delete the `archived` line and archive whichever lot it replaces.
            name: 'Aceh Avatara',
            price: '40',
            archived: '2026-09-19',
            credit: 'Ateng/Tim-Tim/Lini-S · CM Natural · Salésol',
            notes: 'Red grape, ripe stone fruit, honey',
            origin: 'Aceh, Sumatra · Indonesia',
            ll: [[4.63, 96.85]],
          },
          {
            name: 'Nensebo Refisa',
            price: '50',
            credit: 'Kurume/Heirloom · Washed · Salésol',
            notes: 'Citrus, peach, jasmine',
            origin: 'Nensebo, West Arsi · Ethiopia',
            ll: [[6.45, 38.85]],
          },
          {
            name: 'Ethiopia Rumudamo',
            price: '60',
            credit: '74112/Heirloom · Natural · Salésol',
            notes: 'Strawberry, berries, floral finish',
            origin: 'Ethiopia',
            ll: [[6.7, 38.6]],
          },
          {
            // Off the bar September '26, replaced by Nensebo Refisa.
            // Delete the `archived` line to bring it back.
            name: 'Ethiopia Bensa Shantawene',
            price: '50',
            archived: '2026-09-19',
            credit: 'Heirloom · Washed · Salésol',
            notes: 'Citrus, stone fruit, honey',
            origin: 'Bensa, Sidama · Ethiopia',
            ll: [[6.52, 38.72]],
          },
        ],
      },
    ],
  },

  // -------------------------------------------------------------------
  // 3. ESPRESSO · MILK-BASED  ("On Our Whites" sidebar)
  // -------------------------------------------------------------------
  {
    slug: 'espresso/milk',
    kind: 'menu',
    runner: 'I. Espresso',
    eyebrow: 'I. Espresso',
    headline: { size: 'lg', italic: 'Milk-based' },
    lede: 'Lots roasted slightly more developed, softer acidity, fuller body, to pair with the milk treatment.',
    ledeId: 'Lot yang di-roast sedikit lebih developed. Acidity lebih lembut, body lebih penuh, supaya menyatu dengan milk treatment.',
    ledeLayout: 'side',
    pagemark: { section: 'ESPRESSO' },
    modifierKey: 'espressoMilk',
    modifier: 'Iced +5 · Oat / almond +15',
    // `tight` = third density tier. Milk-based packs sidebar +
    // side-lede + 5 lots across 2 categories + modifier — the most
    // dense page in the deck. Opts into smaller fonts + tighter
    // spacing so the bottom row (La Trinidad) renders without
    // overflow on iPhone-portrait. See MenuPage / Lot / Category /
    // Sidebar handling of `tight`.
    tight: true,
    sidebar: {
      label: 'On Our\nWhites',
      text: 'By freeze-distilling our milk, we achieve a velvet-like texture and a deep, honeyed sweetness that standard dairy cannot match.',
      textId: 'Milk kami di-freeze-distill — teksturnya velvet, sweetness-nya dalam seperti madu. Dairy biasa tidak sampai ke sana.',
    },
    categories: [
      {
        label: 'Signature White',
        lots: [
          {
            name: 'Orthodox',
            price: '45',
            credit: 'Brazil, Colombia · Natural, Washed · ST. ALi Melbourne',
            notes: 'Almond, milk chocolate, low acidity',
            origin: 'Brazil & Colombia',
            ll: [[-21.5, -45.0], [2.5, -75.5]],
            roaster: 'mel',
          },
          {
            name: 'Ethiopia Bensa Bombe',
            price: '60',
            credit: '74110, 74112, Local Landraces · Natural · Salésol',
            notes: 'Berries, floral honey, milk chocolate',
            origin: 'Bensa, Sidama · Ethiopia',
            ll: [[6.5, 38.7]],
          },
          {
            // Sold out September '26; a replacement is coming.
            // Delete the `archived` line to bring it back.
            name: 'Raspberry Candy',
            price: '70',
            archived: '2026-09-19',
            credit: 'Mixed Ethiopian · Natural, Supernatural · ONA Sydney',
            notes: 'Sweet berries, creamy white chocolate',
            origin: 'Ethiopia',
            ll: [[6.7, 38.6]],
            roaster: 'syd',
          },
        ],
      },
      {
        label: 'Reserve White',
        lots: [
          {
            name: 'La Rosa',
            price: '80',
            credit: 'Pink Bourbon, Pacamara · Thermal Shock Natural · Salésol',
            notes: 'Berry compote, floral rose, silky and coating',
            origin: 'Colombia',
            ll: [[2.5, -75.5]],
          },
          {
            // Off the bar September '26.
            // Delete the `archived` line to bring it back.
            name: 'La Trinidad',
            price: '110',
            archived: '2026-09-19',
            credit: 'Pink Bourbon, Ombligón, Pacamara · Thermal Shock Natural · Salésol',
            notes: 'Raspberry mousse, cherry, white floral, milk chocolate',
            origin: 'Colombia',
            ll: [[2.5, -75.5]],
          },
        ],
      },
    ],
  },

  // -------------------------------------------------------------------
  // 4. FILTER · MICROLOTS
  // -------------------------------------------------------------------
  {
    slug: 'filter/microlots',
    kind: 'menu',
    runner: 'II. Filter',
    eyebrow: 'II. Filter',
    headline: { size: 'lg', prefix: 'The ', italic: 'microlots' },
    pagemark: { section: 'FILTER' },
    modifierKey: 'filter',
    modifier: 'Iced +5',
    categories: [
      {
        label: 'Indonesian Exotics',
        lede: "Indonesian microlots from producers we're proud to carry, all recurrent Indonesia COE finalists.",
        ledeId: 'Microlot Indonesia dari producer yang kami bangga bisa bawa. Semuanya langganan finalis Indonesia COE.',
        lots: [
          {
            name: 'Aceh Ribang Gayo',
            price: '50',
            credit: 'Ateng/Bourbon/P88 · Natural · Salésol',
            notes: 'Mixed berries, tropical, brown sugar',
            origin: 'Aceh, Sumatra · Indonesia',
            ll: [[4.63, 96.85]],
          },
          {
            // Ran July–September '26 in Ribang Gayo's place. Kept for reinstatement.
            name: 'Aceh Avatara',
            price: '50',
            archived: '2026-09-19',
            credit: 'Ateng/Tim-Tim/Lini-S · CM Natural · Salésol',
            notes: 'Red grape, ripe stone fruit, honey',
            origin: 'Aceh, Sumatra · Indonesia',
            ll: [[4.63, 96.85]],
          },
        ],
      },
      {
        label: 'East Africa',
        lede: 'The floral, complex, tea-like end of the spectrum.',
        ledeId: 'Ujung spektrum yang floral, kompleks, dan tea-like.',
        lots: [
          {
            name: 'Nensebo Refisa',
            price: '60',
            credit: 'Kurume/Heirloom · Washed · Salésol',
            notes: 'Citrus, peach, jasmine',
            origin: 'Nensebo, West Arsi · Ethiopia',
            ll: [[6.45, 38.85]],
          },
          {
            name: 'Ethiopia Rumudamo',
            price: '70',
            credit: '74112/Heirloom · Natural · Salésol',
            notes: 'Strawberry, berries, floral finish',
            origin: 'Ethiopia',
            ll: [[6.7, 38.6]],
          },
          {
            // Off the bar September '26, replaced by Nensebo Refisa.
            name: 'Ethiopia Bensa Shantawene',
            price: '60',
            archived: '2026-09-19',
            credit: 'Heirloom · Washed · Salésol',
            notes: 'Citrus, stone fruit, honey',
            origin: 'Bensa, Sidama · Ethiopia',
            ll: [[6.52, 38.72]],
          },
          {
            name: 'Ethiopia Hamasho Village',
            price: '70',
            credit: '74158 · Natural · Salésol',
            notes: 'Mandarin, peach, red berries, white floral',
            origin: 'Bensa, Sidama · Ethiopia',
            ll: [[6.55, 38.75]],
          },
        ],
      },
    ],
  },

  // -------------------------------------------------------------------
  // 5. FILTER · EXPERIMENTAL EXOTICS
  // -------------------------------------------------------------------
  {
    slug: 'filter/experimental',
    kind: 'menu',
    runner: 'II. Filter',
    eyebrow: 'II. Filter',
    headline: { size: 'md', prefix: 'The ', italic: 'experimental exotics' },
    lede: 'Precision over funk. Defined fruit, dessert sweetness, never boozy.',
    ledeId: 'Presisi, bukan funk. Fruit tajam, dessert sweetness, tanpa boozy.',
    pagemark: { section: 'FILTER' },
    modifierKey: 'filter',
    modifier: 'Iced +5',
    categories: [
      {
        label: 'Experimental',
        lots: [
          {
            name: 'Las Flores Diego Vergara',
            price: '80',
            credit: 'Java · Oxidation Washed · Salésol',
            notes: 'Mandarin, strawberry, floral',
            origin: 'Huila · Colombia',
            ll: [[1.8, -75.9]],
          },
          {
            name: 'Las Flores Jhoan Vergara',
            price: '110',
            credit: 'Red Bourbon · Thermal Shock Natural · Salésol',
            notes: 'Banana, candy, mixed berry',
            origin: 'Huila · Colombia',
            ll: [[1.8, -75.9]],
          },
          {
            name: 'Jardines del Edén Felipe Arcila',
            price: '110',
            credit: 'Sudan Rume · EF2 Natural · Salésol',
            notes: 'Red berries, nectarine, jasmine finish',
            origin: 'Quindío · Colombia',
            ll: [[4.5, -75.7]],
          },
          {
            name: 'Finca Zarza Jhonatan Gasca',
            price: '110',
            credit: 'Pacamara · Thermal Shock Natural · Salésol',
            notes: 'Black cherry, raspberry, milk chocolate',
            origin: 'Huila · Colombia',
            ll: [[2.3, -75.6]],
          },
        ],
      },
    ],
  },

  // -------------------------------------------------------------------
  // 6. FILTER · EXOTIC ESTATES
  // -------------------------------------------------------------------
  {
    slug: 'filter/estates',
    kind: 'menu',
    runner: 'II. Filter',
    eyebrow: 'II. Filter',
    headline: { size: 'md', prefix: 'The ', italic: 'exotic estates' },
    lede: 'The estates that built modern Geisha. Terroir, not technique.',
    ledeId: 'Estate yang membentuk Geisha modern. Terroir, bukan teknik.',
    pagemark: { section: 'FILTER' },
    modifierKey: 'filter',
    modifier: 'Iced +5',
    categories: [
      {
        label: 'Exotic',
        lots: [
          {
            name: 'Gesha Village Red Label Lot #118',
            price: '125',
            credit: 'Ethiopian Gesha 1931 · Natural',
            notes: 'Magnolia, bergamot, apricot',
            origin: 'Bench Maji · Ethiopia',
            ll: [[6.3, 35.4]],
          },
          {
            name: "Finca Solidaridad Nicaragua '25 COE #2",
            price: '125',
            credit: 'Maracaturra · Washed · 2nd place Nicaragua COE 2025',
            notes: 'Kiwi, apricot, floral finish',
            origin: 'Nicaragua',
            ll: [[13.7, -86.5]],
          },
          {
            name: 'Finca Lerida Montana Lot #5',
            price: '150',
            credit: 'Panama Geisha · Honey',
            notes: 'Citrus, peach, jasmine',
            origin: 'Boquete · Panama',
            ll: [[8.78, -82.44]],
          },
          {
            name: 'Iris Estate Odyssey',
            price: '150',
            credit: 'Panama Geisha · Nitrogen Maceration Natural',
            notes: 'Orange, peach, tropical',
            origin: 'Panama',
            ll: [[8.8, -82.45]],
          },
        ],
      },
    ],
  },

  // -------------------------------------------------------------------
  // 7. BACK COVER (rust)
  // -------------------------------------------------------------------
  { slug: 'back', kind: 'cover-back' },
];

// Theme + content for the coffee book covers + slipcase.
export const coffeeTheme = {
  bookId: 'coffee',
  routeBase: '/coffee',
  coverColor: '#A85328', // rust
  coverColorAccent: '#8E4422', // rust-deep
  accentColor: '#E0B97A', // rust-accent
  bookTitle: 'COFFEE MENU',
  // Cover copy — overrides config.brand defaults if set
  frontCover: {
    hero: ['Coffee,', 'treated as it', 'ought to be.'],
    tag: 'COFFEE MENU',
  },
  backCover: {
    hero: ['Roasted in', 'BSD City.'],
  },
};
